// game.js — ゲーム進行と描画

import { W, H, BALL_R, ARC, LAUNCH_POS, createBoard, dialToPower, dialSide } from './board.js';
import { DEFAULT_MACHINE, currencyOf, currencyLabel } from './machines.js';
// このファイルでは局所変数の t が経過フレームを指すので、取り込みは tr にする
import { t as tr, num } from './i18n.js';
import { stepWorld, launchBall } from './physics.js';
import { AudioEngine } from './audio.js';
import { FX } from './fx.js';
import { VideoDeck } from './video.js';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;

// ---------------------------------------------------------------- 設定

// 機種ごとに変わる値は js/machines.js 側にある。ここは全機種共通のもの。
// 初期値と切れたときの補充は通貨（玉／メダル）ごとに違う。
const MAX_BALLS_ON_BOARD = 28;
const MAX_PENDING_SPINS = 4;

const SYMBOLS = [
  { label: '1', color: '#5ac8fa' },
  { label: '2', color: '#5cdb95' },
  { label: '3', color: '#ffd166' },
  { label: '5', color: '#ff9f6e' },
  { label: '7', color: '#ff4d8d' },   // プレミア
  { label: '★', color: '#c77dff' },
  { label: '◆', color: '#4dd8ff' },
];
const PREMIUM = 4;
const NSYM = SYMBOLS.length;

const PARTY = ['#ff4d8d', '#ffd166', '#5ac8fa', '#5cdb95', '#c77dff', '#ffffff'];

// ---------------------------------------------------------------- 本体

export class Game {
  constructor(canvas, hud, machine = DEFAULT_MACHINE) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.hud = hud;
    this.machine = machine;
    this.audio = new AudioEngine();
    this.fx = new FX();

    // ブルーム用の低解像度バッファ
    this.glowCv = document.createElement('canvas');
    this.glowCv.width = W / 2;
    this.glowCv.height = H / 2;
    this.glow = this.glowCv.getContext('2d');
    this.supportsFilter = typeof this.ctx.filter === 'string';

    this.board = createBoard(machine);
    this.balls = [];
    this.deck = null;
    this._videoState = null;
    this.frame = 0;
    this.paused = false;
    this._initVideo();          // 復元した機種が映像機種のこともある

    this.reset();
    this._resize();
    window.addEventListener('resize', () => this._resize());
  }

  /** この台で使う通貨（正規の台は玉、自作台はメダル） */
  get currency() { return currencyOf(this.machine); }

  // ホールで別の台に座り直す。同じ通貨の台どうしなら持ち玉を引き継ぐ。
  loadMachine(machine, keepStock = true) {
    const carried = keepStock ? this.stock : currencyOf(machine).start;
    this.machine = machine;
    this.board = createBoard(machine);
    this._initVideo();
    this.reset();
    this.stock = carried;
    return this;
  }

  // 映像を持つ機種だけデッキを用意する
  _initVideo() {
    if (this.deck) { this.deck.dispose(); this.deck = null; }
    this._videoState = null;
    if (this.machine.video) this.deck = new VideoDeck(this.machine.video);
  }

  // ホール表示中などは映像も止める
  setPaused(v) {
    this.paused = v;
    if (!this.deck) return;
    if (v) this.deck.pause(); else this.deck.resume();
  }

  // 盤面の状態に応じて流す映像を切り替える
  _syncVideo() {
    if (!this.deck) return;
    const r = this.reel;
    const want = this.mode === 'jackpot' ? 'jackpot'
      : this.mode === 'reach' ? 'reach'
      : r.state === 'spin' ? 'spin'
      : 'idle';
    // 手が空いているときに次の映像を 1 本ずつ温めておく
    if (this.frame % 30 === 0) this.deck.prefetchNext();
    if (want === this._videoState) return;
    this._videoState = want;
    // リーチと大当りは頭出しして見せ場を最初から流す
    this.deck.setState(want, { restart: want === 'reach' || want === 'jackpot' });
  }

  reset() {
    this.stock = this.currency.start;
    this.launched = 0;
    this.spins = 0;
    this.jackpots = 0;
    this.pendingSpins = 0;
    this.dial = 0.2;
    this.power = dialToPower(this.dial);
    this.side = dialSide(this.dial);
    this.firing = false;
    this.fireCooldown = 0;
    this.balls.length = 0;
    this.board.gateOpen = false;
    this.fx.reset();

    this.mode = 'normal';          // normal | reach | jackpot
    this.banner = null;
    this.payoutFloat = 0;

    this.reel = {
      state: 'idle',               // idle | spin | result
      pos: [0, 2, 4],
      speed: [0, 0, 0],
      target: [0, 0, 0],
      stopFrame: [0, 0, 0],
      stopFrom: [0, 0, 0],
      stopTo: [0, 0, 0],
      stopped: [true, true, true],
      t: 0,
      win: false,
      premium: false,
      reach: false,
      superReach: false,
    };

    this.jp = { active: false, round: 0, rounds: 0, ballsIn: 0, timer: 0, phase: 'none', total: 0 };
    this._setMusic('normal');
  }

  _setMusic(m) { this.audio.setMusic(m); }

  // CSS がどんな寸法を与えてきても歪ませない（余りは上下左右に振り分ける）
  _resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const rect = this.canvas.getBoundingClientRect();
    const cssW = rect.width || W;
    const cssH = rect.height || H;
    this.canvas.width = Math.max(1, Math.round(cssW * dpr));
    this.canvas.height = Math.max(1, Math.round(cssH * dpr));
    this.scale = Math.min(cssW / W, cssH / H) * dpr;
    this.offX = (this.canvas.width - W * this.scale) / 2;
    this.offY = (this.canvas.height - H * this.scale) / 2;
  }

  // -------------------------------------------------------------- 入力

  // ハンドルの目盛り(0..1)を受け取る。実際の発射力への変換は board 側。
  setPower(dial) {
    this.dial = clamp(dial, 0, 1);
    this.power = dialToPower(this.dial);
    this.side = dialSide(this.dial);
  }

  /** 今どちらを打つべきか。大当り中はアタッカーが右にあるので右打ち。 */
  get recommendedSide() { return this.mode === 'jackpot' ? 'right' : 'left'; }

  /** 打ち方が合っていないか（合っていないと出玉にならない） */
  get wrongSide() { return this.side !== 'mid' && this.side !== this.recommendedSide; }

  setFiring(on) {
    if (on) this.audio.init();
    if (on && !this.firing) this.fireCooldown = 0;
    this.firing = on;
  }

  toggleMute() {
    this.audio.init();
    this.audio.setMuted(!this.audio.muted);
    return this.audio.muted;
  }

  // -------------------------------------------------------------- 進行

  _fire() {
    if (this.stock <= 0) {
      if (this.frame % 60 === 0) this.audio.empty();
      return;
    }
    if (this.balls.length >= MAX_BALLS_ON_BOARD) return;
    this.stock--;
    this.launched++;
    const b = launchBall(this.power, Math.random());
    this.balls.push(b);
    this.audio.launch(this.power);
    this.fx.spark(LAUNCH_POS.x, LAUNCH_POS.y, 5, '#9fe8ff', 2, 14);
  }

  _award(n, x, y, color) {
    this.stock += n;
    this.payoutFloat += n;
    this.fx.text(x, y, `+${n}`, color, 20, 54);
    for (let i = 0; i < Math.min(n, 6); i++) this.audio.payout();
  }

  _onPocket(pk, x, y) {
    if (pk.id === 'start') {
      this._award(pk.award, x, y - 16, '#ffe27a');
      this.audio.pocketStart();
      this.fx.burst(x, y, 22, ['#ffe27a', '#fff', '#ffb84d'], 4.5, 40);
      this.fx.ring(x, y, '#ffe27a', 54, 30, 4);
      this.fx.addShake(3);
      if (this.pendingSpins < MAX_PENDING_SPINS) this.pendingSpins++;
    } else if (pk.id === 'attacker') {
      this._award(pk.award, x, y - 18, '#ff6ec7');
      this.audio.attackerHit();
      this.fx.burst(x, y, 30, PARTY, 6, 46);
      this.fx.ring(x, y, '#ff6ec7', 76, 30, 5);
      this.fx.addShake(7);
      this.fx.addFlash(0.16, '#ff8ad4');
      if (this.jp.active && this.jp.phase === 'round') {
        this.jp.ballsIn++;
        this.jp.total += pk.award;
      }
    } else {
      this._award(pk.award, x, y - 14, '#8ef2ff');
      this.audio.pocketSide();
      this.fx.burst(x, y, 12, ['#8ef2ff', '#fff'], 3.2, 30);
      this.fx.ring(x, y, '#8ef2ff', 36, 22, 3);
    }
  }

  _handleEvents(events) {
    for (const e of events) {
      switch (e.type) {
        case 'peg':
          this.audio.peg(e.power);
          if (e.power > 2.2) {
            this.fx.spark(e.x, e.y, 2, '#cfefff', 1.6, 12);
            if (e.power > 5) this.fx.ring(e.x, e.y, '#9fe8ff', 16, 12, 2);
          }
          break;
        case 'ball':
          this.audio.ballClack(e.power);
          if (e.power > 3) this.fx.spark(e.x, e.y, 2, '#ffffff', 1.5, 10);
          break;
        case 'windmill':
          if (e.power > 1.5) { this.audio.windmill(); this.fx.spark(e.x, e.y, 3, '#b39dff', 2, 14); }
          break;
        case 'pocket':
          this._onPocket(e.pocket, e.x, e.y);
          break;
        case 'drain':
          this.audio.drain();
          this.fx.spark(e.x, e.y, 4, '#5a6a8a', 1.4, 16);
          break;
        case 'rail-return':
          this.stock++;                 // 発射失敗は返却
          this.audio.railReturn();
          break;
      }
    }
  }

  // -------------------------------------------------------------- 抽選

  _startSpin() {
    const r = this.reel;
    this.pendingSpins--;
    this.spins++;

    const win = Math.random() < 1 / this.machine.odds;
    let a, b, c;
    if (win) {
      a = b = c = (Math.random() * NSYM) | 0;
    } else if (Math.random() < this.machine.reachChance) {
      a = b = (Math.random() * NSYM) | 0;
      do { c = (Math.random() * NSYM) | 0; } while (c === a);
    } else {
      a = (Math.random() * NSYM) | 0;
      do { b = (Math.random() * NSYM) | 0; } while (b === a);
      c = (Math.random() * NSYM) | 0;
    }

    r.win = win;
    r.premium = win && a === PREMIUM;
    r.reach = a === b;
    r.superReach = r.reach && Math.random() < this.machine.superReachChance;
    r.target = [a, b, c];
    r.state = 'spin';
    r.t = 0;
    r.stopped = [false, false, false];
    r.speed = [0.44, 0.44, 0.44];
    // リーチなら3つ目を大きく引き延ばす
    const extra = r.reach ? (r.superReach ? 460 : 230) : 0;
    r.stopFrame = [56, 92, 132 + extra];
    this.audio.reelSpin();
  }

  _updateReel() {
    const r = this.reel;
    if (r.state === 'idle') {
      if (this.pendingSpins > 0 && !this.jp.active) this._startSpin();
      return;
    }
    if (r.state === 'result') {
      if (--r.t <= 0) {
        r.state = 'idle';
        if (this.mode !== 'jackpot') { this.mode = 'normal'; this._setMusic('normal'); }
      }
      return;
    }

    r.t++;
    for (let i = 0; i < 3; i++) {
      if (r.stopped[i]) continue;
      const sf = r.stopFrame[i];
      const STOP_DUR = 20;

      if (r.t < sf) {
        // リーチ中の3リール目はゆっくり回す
        let sp = r.speed[i];
        if (i === 2 && r.reach && r.t > 132) sp = r.superReach ? 0.055 : 0.10;
        r.pos[i] += sp;
        if (this.frame % 4 === 0 && i === 2 && r.reach) this.audio.reelTick();
        else if (this.frame % 3 === 0 && !r.reach) this.audio.reelTick();
      } else if (r.t === sf) {
        r.stopFrom[i] = r.pos[i];
        r.stopTo[i] = this._landing(r.pos[i], r.target[i]);
      }

      if (r.t >= sf) {
        const k = clamp((r.t - sf) / STOP_DUR, 0, 1);
        r.pos[i] = lerp(r.stopFrom[i], r.stopTo[i], easeOutBack(k));
        if (k >= 1) {
          r.pos[i] = r.stopTo[i];
          r.stopped[i] = true;
          this.audio.reelStop(i);
          this.fx.addShake(2.5);
          const rx = 225 + (i - 1) * (this.deck ? 46 : 71);
          this.fx.ring(rx, 268, SYMBOLS[r.target[i]].color, 40, 20, 3);

          // 2つ揃った瞬間にリーチ演出へ
          if (i === 1 && r.reach) {
            this.mode = 'reach';
            this._setMusic('reach');
            this.fx.addFlash(0.3, '#ff5a7a');
            this.fx.addShake(9);
            this.fx.chroma = 1;
            this.banner = {
              text: tr(r.superReach ? 'game.superReach' : 'game.reach'),
              life: 90, max: 90, color: '#ff5a7a',
            };
            if (r.superReach) this.audio.superReach(); else this.audio.reach();
          }
          if (i === 2) this._resolveSpin();
        }
      }
    }
  }

  _landing(pos, target) {
    // 現在位置より必ず先で、中央が target になる整数位置
    let p = Math.ceil(pos) + 3;
    while (((p % NSYM) + NSYM) % NSYM !== target) p++;
    return p;
  }

  _resolveSpin() {
    const r = this.reel;
    r.state = 'result';
    r.t = r.win ? 1 : 46;
    if (r.win) this._beginJackpot();
    else {
      this.audio.lose();
      this.mode = 'normal';
      this._setMusic('normal');
      this.banner = { text: tr('game.lose'), life: 40, max: 40, color: '#8899bb' };
    }
  }

  // -------------------------------------------------------------- 大当り

  _beginJackpot() {
    const r = this.reel;
    this.jackpots++;
    this.mode = 'jackpot';
    this._setMusic('jackpot');
    this.jp = {
      active: true,
      rounds: r.premium ? this.machine.premiumRounds : this.machine.rounds,
      round: 0,
      ballsIn: 0,
      timer: 0,
      phase: 'intro',
      total: 0,
    };
    this.audio.jackpot();
    this.fx.addFlash(1, '#ffffff');
    this.fx.addShake(24);
    this.fx.rays = 1;
    this.fx.chroma = 1;
    this.fx.confetti(W, 120, PARTY);
    this.fx.burst(W / 2, 268, 90, PARTY, 11, 70);
    this.banner = {
      text: tr(r.premium ? 'game.jackpotPremium' : 'game.jackpot'),
      life: 170, max: 170, color: r.premium ? '#ffd166' : '#ff4d8d', big: true,
    };
  }

  _updateJackpot() {
    const j = this.jp;
    if (!j.active) return;
    j.timer++;

    if (j.phase === 'intro') {
      this.fx.rays = Math.min(1, this.fx.rays + 0.02);
      if (j.timer > 150) { j.phase = 'interval'; j.timer = 0; }
    } else if (j.phase === 'interval') {
      if (j.timer > 70) {
        j.round++;
        if (j.round > j.rounds) {
          j.phase = 'end';
          j.timer = 0;
          this.banner = {
            text: tr('game.gotBalls', { n: num(j.total) }),
            life: 150, max: 150, color: '#ffd166', big: true,
          };
          this.audio.roundStart(9);
        } else {
          j.phase = 'round';
          j.timer = 0;
          j.ballsIn = 0;
          this.board.gateOpen = true;
          this.audio.gate(true);
          this.audio.roundStart(j.round);
          this.fx.addFlash(0.3, '#ffd166');
          this.fx.addShake(8);
          const atk = this.board.pockets.find((p) => p.id === 'attacker');
          this.fx.ring(atk.cx, atk.cy - 14, '#ffd166', 120, 34, 6);
          this.banner = {
            text: tr('game.round', { n: j.round, total: j.rounds }),
            life: 70, max: 70, color: '#ffd166',
          };
        }
      }
    } else if (j.phase === 'round') {
      this.fx.rays = Math.min(1, this.fx.rays + 0.02);
      if (this.frame % 7 === 0) this.fx.confetti(W, 2, PARTY);
      if (j.ballsIn >= this.machine.roundMaxBalls || j.timer > this.machine.roundTime) {
        this.board.gateOpen = false;
        this.audio.gate(false);
        j.phase = 'interval';
        j.timer = 0;
      }
    } else if (j.phase === 'end') {
      this.fx.rays = Math.max(0, this.fx.rays - 0.012);
      if (j.timer > 150) {
        j.active = false;
        j.phase = 'none';
        this.mode = 'normal';
        this._setMusic('normal');
        this.fx.rays = 0;
      }
    }
  }

  // -------------------------------------------------------------- 更新

  update() {
    if (this.paused) return;
    this.frame++;

    if (this.firing) {
      if (--this.fireCooldown <= 0) {
        this._fire();
        this.fireCooldown = this.machine.fireInterval;
      }
    }

    const events = [];
    stepWorld(this.board, this.balls, events);
    this._handleEvents(events);
    for (let i = this.balls.length - 1; i >= 0; i--) {
      if (!this.balls[i].alive) this.balls.splice(i, 1);
    }

    this._updateReel();
    this._updateJackpot();
    this._syncVideo();
    this.fx.update();

    if (this.stock <= 0 && this.balls.length === 0) {
      const cur = this.currency;
      this.stock = cur.service;
      this.audio.roundStart(1);
      this.fx.addFlash(0.2, '#8ef2ff');
      this.banner = {
        text: tr(`game.service.${cur.id}`, { n: cur.service }),
        life: 110, max: 110, color: '#8ef2ff',
      };
    }

    if (this.banner && --this.banner.life <= 0) this.banner = null;
    if (this.payoutFloat > 0) this.payoutFloat = Math.max(0, this.payoutFloat - 0.6);

    this._updateHud();
  }

  _updateHud() {
    const h = this.hud;
    if (!h) return;
    h.stock.textContent = num(this.stock);
    const label = currencyLabel(this.currency);
    if (h.stockLabel && h.stockLabel.textContent !== label) h.stockLabel.textContent = label;
    h.jackpots.textContent = this.jackpots;
    h.spins.textContent = this.spins;
    h.pending.textContent = this.pendingSpins;
    const cls = this.mode === 'jackpot' ? 'jp' : this.mode === 'reach' ? 'reach' : '';
    if (h.root.dataset.mode !== cls) h.root.dataset.mode = cls;
  }

  // -------------------------------------------------------------- 描画

  render() {
    const ctx = this.ctx;
    const g = this.glow;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offX, this.offY);
    g.setTransform(0.5, 0, 0, 0.5, 0, 0);
    g.clearRect(0, 0, W, H);

    const [sx, sy] = this.fx.shakeOffset();
    ctx.save();
    ctx.translate(sx, sy);

    this._drawBackground(ctx);
    this.fx.drawRays(ctx, W / 2, 300, 520);
    this._drawBoard(ctx, g);
    this._drawLCD(ctx, g);
    this._drawBalls(ctx, g);
    this.fx.drawRings(ctx, g);
    this.fx.drawParticles(ctx, g);

    ctx.restore();

    // ブルーム合成
    if (this.supportsFilter) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.filter = 'blur(7px)';
      ctx.globalAlpha = 0.85;
      ctx.drawImage(this.glowCv, 0, 0, W, H);
      ctx.filter = 'blur(18px)';
      ctx.globalAlpha = 0.5;
      ctx.drawImage(this.glowCv, 0, 0, W, H);
      ctx.restore();
    } else {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.5;
      ctx.drawImage(this.glowCv, 0, 0, W, H);
      ctx.restore();
    }

    ctx.save();
    ctx.translate(sx, sy);
    this.fx.drawTexts(ctx);
    this._drawBanner(ctx);
    ctx.restore();

    this._drawSideHint(ctx);
    this.fx.drawFlash(ctx, W, H);
    this._drawVignette(ctx);
    this._drawBezel(ctx);
  }

  _drawBackground(ctx) {
    const t = this.frame;
    const grd = ctx.createLinearGradient(0, 0, 0, H);
    if (this.mode === 'jackpot') {
      grd.addColorStop(0, '#2a0a3c');
      grd.addColorStop(0.5, '#3d0f2e');
      grd.addColorStop(1, '#12061f');
    } else if (this.mode === 'reach') {
      grd.addColorStop(0, '#2a0c1a');
      grd.addColorStop(0.5, '#1a0a20');
      grd.addColorStop(1, '#0a0614');
    } else {
      const h = this.machine.hue;
      grd.addColorStop(0, `hsl(${h} 55% 13%)`);
      grd.addColorStop(0.5, `hsl(${h} 50% 8%)`);
      grd.addColorStop(1, '#05060f');
    }
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);

    // 流れるネオングリッド
    ctx.save();
    ctx.globalAlpha = 0.10;
    ctx.strokeStyle = this.mode === 'jackpot' ? '#ff9ad5' : this.machine.color;
    ctx.lineWidth = 1;
    const off = (t * 0.4) % 40;
    ctx.beginPath();
    for (let y = -40 + off; y < H; y += 40) { ctx.moveTo(0, y); ctx.lineTo(W, y); }
    for (let x = 0; x < W; x += 40) { ctx.moveTo(x, 0); ctx.lineTo(x, H); }
    ctx.stroke();
    ctx.restore();
  }

  // 盤面の額縁。CSS の border/box-shadow でやると、レイアウトの都合で
  // キャンバスを枠いっぱいに広げる必要があり位置が合わなくなるため、
  // 論理座標のままここで描く。
  _drawBezel(ctx) {
    const t = this.frame;
    const col = this.mode === 'jackpot' ? `hsl(${(t * 4) % 360} 100% 65%)`
      : this.mode === 'reach' ? '#ff5a7a' : this.machine.color;
    const glow = this.mode === 'normal' ? 16 : 26 + Math.sin(t * 0.3) * 8;

    ctx.save();
    ctx.lineJoin = 'round';
    ctx.shadowColor = col;
    ctx.shadowBlur = glow;
    ctx.strokeStyle = col;
    ctx.globalAlpha = 0.9;
    ctx.lineWidth = 3;
    roundRect(ctx, 1.5, 1.5, W - 3, H - 3, 16);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(6,8,18,0.9)';
    ctx.lineWidth = 2;
    roundRect(ctx, 4, 4, W - 8, H - 8, 14);
    ctx.stroke();
    ctx.restore();
  }

  _drawVignette(ctx) {
    const g = ctx.createRadialGradient(W / 2, H / 2, H * 0.30, W / 2, H / 2, H * 0.72);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.55)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  _drawBoard(ctx, glow) {
    const t = this.frame;
    const board = this.board;
    const hue = this.mode === 'jackpot' ? (t * 3) % 360
      : this.mode === 'reach' ? 345 : this.machine.hue;
    const neon = `hsl(${hue} 100% 62%)`;

    // 盤面のガラス面
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(25, 700);
    ctx.lineTo(25, 250);
    for (let a = 180; a >= 0; a -= 4) {
      ctx.lineTo(ARC.cx + ARC.rOut * Math.cos(a * Math.PI / 180), ARC.cy - ARC.rOut * Math.sin(a * Math.PI / 180));
    }
    ctx.lineTo(425, 800);
    ctx.lineTo(25, 800);
    ctx.closePath();
    ctx.fillStyle = 'rgba(10,16,38,0.55)';
    ctx.fill();
    ctx.restore();

    // 外枠・レール
    ctx.lineCap = 'round';
    for (const s of board.segments) {
      if (s.id === 'gate' && board.gateOpen) continue;
      let col, wdt, gl = true;
      switch (s.kind) {
        case 'rail': col = '#7fd4ff'; wdt = 3; break;
        case 'canopy': col = '#9d7dff'; wdt = 3; break;
        case 'wall': col = '#6ea8ff'; wdt = 3; break;
        case 'panel': col = 'rgba(120,160,255,0.5)'; wdt = 2; gl = false; break;
        case 'cup': col = '#ffd166'; wdt = 3; break;
        case 'gate': col = '#ff6ec7'; wdt = 5; break;
        default: col = '#889'; wdt = 2; gl = false;
      }
      ctx.strokeStyle = col;
      ctx.lineWidth = wdt;
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
      ctx.stroke();
      if (gl) {
        glow.strokeStyle = col;
        glow.lineWidth = wdt * 0.8;
        glow.beginPath();
        glow.moveTo(s.x1, s.y1);
        glow.lineTo(s.x2, s.y2);
        glow.stroke();
      }
    }

    // アタッカーが開いている間は開口部を光らせる
    if (board.gateOpen) {
      const p = 0.6 + 0.4 * Math.sin(t * 0.25);
      ctx.save();
      ctx.globalAlpha = p;
      const atk = board.pockets.find((q) => q.id === 'attacker');
      const g2 = ctx.createLinearGradient(0, atk.y1 - 14, 0, atk.y2);
      g2.addColorStop(0, 'rgba(255,110,199,0.0)');
      g2.addColorStop(1, 'rgba(255,110,199,0.85)');
      ctx.fillStyle = g2;
      ctx.fillRect(atk.x1 - 2, atk.y1 - 14, atk.x2 - atk.x1 + 4, atk.y2 - atk.y1 + 14);
      ctx.restore();
      glow.globalAlpha = p;
      glow.fillStyle = '#ff6ec7';
      glow.fillRect(atk.x1, atk.y1, atk.x2 - atk.x1, atk.y2 - atk.y1);
      glow.globalAlpha = 1;
    }

    // 入賞口のラベル
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 10px system-ui, sans-serif';
    for (const pk of board.pockets) {
      if (pk.id === 'attacker' && !board.gateOpen) continue;
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fillText(tr(pk.labelKey), pk.cx, pk.cy + 36);
    }

    // 風車
    for (const wm of board.windmills) {
      ctx.save();
      ctx.translate(wm.x, wm.y);
      ctx.rotate(wm.phase);
      for (let i = 0; i < wm.blades; i++) {
        const a = (i * TAU) / wm.blades;
        ctx.strokeStyle = '#b39dff';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * 4, Math.sin(a) * 4);
        ctx.lineTo(Math.cos(a) * wm.r, Math.sin(a) * wm.r);
        ctx.stroke();
      }
      ctx.fillStyle = '#e8dcff';
      ctx.beginPath(); ctx.arc(0, 0, 4, 0, TAU); ctx.fill();
      ctx.restore();

      glow.save();
      glow.translate(wm.x, wm.y);
      glow.rotate(wm.phase);
      glow.strokeStyle = '#b39dff';
      glow.lineWidth = 3;
      for (let i = 0; i < wm.blades; i++) {
        const a = (i * TAU) / wm.blades;
        glow.beginPath();
        glow.moveTo(Math.cos(a) * 4, Math.sin(a) * 4);
        glow.lineTo(Math.cos(a) * wm.r, Math.sin(a) * wm.r);
        glow.stroke();
      }
      glow.restore();
    }

    // 釘
    for (const p of board.pegs) {
      const f = Math.max(0, p.flash);
      const base = p.type === 'guide' ? '#ffd166' : '#c9d8ff';
      ctx.fillStyle = f > 0 ? '#ffffff' : base;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + f * 1.6, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();
      if (f > 0.05) {
        glow.globalAlpha = f;
        glow.fillStyle = '#ffffff';
        glow.beginPath();
        glow.arc(p.x, p.y, p.r + f * 3, 0, TAU);
        glow.fill();
        glow.globalAlpha = 1;
      } else if (p.type === 'guide') {
        glow.globalAlpha = 0.5;
        glow.fillStyle = base;
        glow.beginPath(); glow.arc(p.x, p.y, p.r, 0, TAU); glow.fill();
        glow.globalAlpha = 1;
      }
    }
  }

  _drawLCD(ctx, glow) {
    const t = this.frame;
    const r = this.reel;

    // 画面
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(118, 222); ctx.lineTo(146, 200); ctx.lineTo(225, 170);
    ctx.lineTo(304, 200); ctx.lineTo(332, 222); ctx.lineTo(332, 306);
    ctx.lineTo(306, 330); ctx.lineTo(144, 330); ctx.lineTo(118, 306);
    ctx.closePath();
    ctx.clip();

    const bg = ctx.createLinearGradient(0, 170, 0, 330);
    if (this.mode === 'jackpot') { bg.addColorStop(0, '#3a0b46'); bg.addColorStop(1, '#12061f'); }
    else if (this.mode === 'reach') { bg.addColorStop(0, '#3d0a1c'); bg.addColorStop(1, '#12040f'); }
    else { bg.addColorStop(0, `hsl(${this.machine.hue} 70% 12%)`); bg.addColorStop(1, '#03060f'); }
    ctx.fillStyle = bg;
    ctx.fillRect(110, 160, 240, 180);

    // 映像を持つ機種は液晶の地を差し替える（読み込み中は上の下地のまま）
    const hasVideo = this.deck && this.deck.draw(ctx, 110, 160, 240, 180);
    if (hasVideo) {
      // 盤面の色味に馴染ませる薄い覆い
      ctx.fillStyle = this.mode === 'jackpot' ? 'rgba(60,0,40,0.18)' : 'rgba(0,6,20,0.22)';
      ctx.fillRect(110, 160, 240, 180);
    }

    // スキャンライン
    ctx.globalAlpha = hasVideo ? 0.06 : 0.10;
    ctx.fillStyle = '#8fd8ff';
    for (let y = 172; y < 330; y += 4) ctx.fillRect(112, y, 236, 1);
    ctx.globalAlpha = 1;

    // リール。映像機種は実機と同じく小さめにして下へ寄せる
    const RW = hasVideo ? 40 : 64;
    const RH = hasVideo ? 46 : 88;
    const RY = hasVideo ? 268 : 224;
    const GAP = hasVideo ? 6 : 7;
    for (let i = 0; i < 3; i++) {
      const rx = 225 - (RW * 3 + GAP * 2) / 2 + i * (RW + GAP);
      ctx.save();
      ctx.beginPath();
      roundRect(ctx, rx, RY, RW, RH, 7);
      ctx.clip();
      ctx.fillStyle = hasVideo ? 'rgba(4,10,24,0.72)' : 'rgba(4,10,24,0.9)';
      ctx.fillRect(rx, RY, RW, RH);

      const pos = r.pos[i];
      const frac = pos - Math.floor(pos);
      const centerIdx = Math.floor(pos);
      const SH = RH;   // 1リールにつき図柄1つを表示する
      const spinning = !r.stopped[i] && r.state === 'spin';
      for (let k = -1; k <= 1; k++) {
        const idx = ((centerIdx + k) % NSYM + NSYM) % NSYM;
        const sy = RY + RH / 2 + (k - frac) * SH;
        if (sy < RY - SH || sy > RY + RH + SH) continue;
        const sym = SYMBOLS[idx];
        ctx.save();
        ctx.translate(rx + RW / 2, sy);
        if (spinning) { ctx.globalAlpha = 0.42; ctx.scale(1, 1.22); }
        ctx.font = `900 ${hasVideo ? 26 : 48}px system-ui, -apple-system, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = sym.color;
        ctx.fillText(sym.label, 0, 0);
        ctx.restore();

        if (!spinning && Math.abs(k - frac) < 0.5) {
          glow.globalAlpha = 0.85;
          glow.font = `900 ${hasVideo ? 26 : 48}px system-ui, sans-serif`;
          glow.textAlign = 'center';
          glow.textBaseline = 'middle';
          glow.fillStyle = sym.color;
          glow.fillText(sym.label, rx + RW / 2, sy);
          glow.globalAlpha = 1;
        }
      }
      ctx.restore();

      // リール枠
      ctx.strokeStyle = r.stopped[i] ? SYMBOLS[r.target[i]].color : 'rgba(140,180,255,0.5)';
      ctx.lineWidth = 2;
      roundRect(ctx, rx, RY, RW, RH, 7);
      ctx.stroke();
    }

    // 上部の状態表示
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (this.mode === 'jackpot') {
      ctx.font = '900 20px system-ui, sans-serif';
      ctx.fillStyle = '#ffd166';
      const j = this.jp;
      ctx.fillText(
        j.phase === 'round'
          ? tr('game.lcd.round', { n: j.round, hit: j.ballsIn, max: this.machine.roundMaxBalls })
          : j.phase === 'end' ? tr('game.lcd.got', { n: num(j.total) })
          : tr('game.lcd.jackpot', { n: j.rounds }),
        225, 200);
    } else if (this.mode === 'reach') {
      const p = 0.5 + 0.5 * Math.sin(t * 0.4);
      ctx.font = '900 20px system-ui, sans-serif';
      ctx.fillStyle = `rgba(255,90,122,${0.5 + p * 0.5})`;
      ctx.fillText(tr(this.reel.superReach ? 'game.lcd.superReach' : 'game.lcd.reach'), 225, 200);
    } else {
      ctx.font = '700 12px system-ui, sans-serif';
      ctx.fillStyle = 'rgba(150,190,255,0.75)';
      ctx.fillText(tr('game.lcd.odds', { n: this.machine.odds }), 225, 198);
    }

    // 保留玉
    for (let i = 0; i < MAX_PENDING_SPINS; i++) {
      const px = 176 + i * 22, py = 320;
      const on = i < this.pendingSpins;
      ctx.beginPath();
      ctx.arc(px, py, 6, 0, TAU);
      ctx.fillStyle = on ? '#ffe27a' : 'rgba(255,255,255,0.12)';
      ctx.fill();
      if (on) {
        glow.globalAlpha = 0.8;
        glow.fillStyle = '#ffe27a';
        glow.beginPath(); glow.arc(px, py, 5, 0, TAU); glow.fill();
        glow.globalAlpha = 1;
      }
    }
    ctx.restore();

    // 画面の縁を光らせる
    const hue = this.mode === 'jackpot' ? (t * 4) % 360
      : this.mode === 'reach' ? 345 : this.machine.hue;
    const pulse = this.mode === 'normal' ? 0.5 : 0.7 + 0.3 * Math.sin(t * 0.3);
    ctx.save();
    ctx.strokeStyle = `hsl(${hue} 100% 65%)`;
    ctx.lineWidth = 3;
    ctx.globalAlpha = pulse;
    ctx.beginPath();
    ctx.moveTo(118, 222); ctx.lineTo(146, 200); ctx.lineTo(225, 170);
    ctx.lineTo(304, 200); ctx.lineTo(332, 222); ctx.lineTo(332, 306);
    ctx.lineTo(306, 330); ctx.lineTo(144, 330); ctx.lineTo(118, 306);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    glow.save();
    glow.globalAlpha = pulse * 0.9;
    glow.strokeStyle = `hsl(${hue} 100% 65%)`;
    glow.lineWidth = 2.5;
    glow.beginPath();
    glow.moveTo(118, 222); glow.lineTo(146, 200); glow.lineTo(225, 170);
    glow.lineTo(304, 200); glow.lineTo(332, 222); glow.lineTo(332, 306);
    glow.lineTo(306, 330); glow.lineTo(144, 330); glow.lineTo(118, 306);
    glow.closePath();
    glow.stroke();
    glow.restore();
  }

  // 「左打ち／右打ち」の案内。間違った打ち方をしていると警告に変わる。
  _drawSideHint(ctx) {
    const t = this.frame;
    const jp = this.mode === 'jackpot';
    const wrong = this.wrongSide;
    if (!jp && !wrong) return;                 // 通常時に正しく打てていれば出さない

    const y = 112;                             // 液晶の外。ラウンド表示と重ねない
    const pulse = 0.6 + 0.4 * Math.sin(t * 0.3);
    const right = this.recommendedSide === 'right';
    const label = tr(right ? 'game.hintRight' : 'game.hintLeft');
    const col = wrong ? '#ff5a5a' : '#ffd166';

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = pulse;

    ctx.font = '900 19px system-ui, sans-serif';
    const w = ctx.measureText(label).width + 34;
    ctx.fillStyle = 'rgba(0,0,0,0.62)';
    roundRect(ctx, 225 - w / 2, y - 13, w, 26, 8);
    ctx.fill();

    ctx.fillStyle = col;
    ctx.fillText(label, 225 + (right ? -8 : 0), y);

    // 進行方向の矢印
    if (right) {
      const ax = 225 + w / 2 - 15;
      ctx.beginPath();
      ctx.moveTo(ax + 7, y);
      ctx.lineTo(ax - 4, y - 7);
      ctx.lineTo(ax - 4, y + 7);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  _drawBalls(ctx, glow) {
    for (const b of this.balls) {
      // 尾
      const tr = b.trail;
      if (tr.length >= 4) {
        ctx.strokeStyle = 'rgba(180,225,255,0.35)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(tr[0], tr[1]);
        for (let i = 2; i < tr.length; i += 2) ctx.lineTo(tr[i], tr[i + 1]);
        ctx.stroke();

        glow.strokeStyle = 'rgba(150,215,255,0.5)';
        glow.lineWidth = 2;
        glow.beginPath();
        glow.moveTo(tr[0], tr[1]);
        for (let i = 2; i < tr.length; i += 2) glow.lineTo(tr[i], tr[i + 1]);
        glow.stroke();
      }

      const g = ctx.createRadialGradient(b.x - 2.4, b.y - 2.8, 0.6, b.x, b.y, BALL_R);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.35, '#dbeeff');
      g.addColorStop(0.75, '#8fb4d8');
      g.addColorStop(1, '#3b5578');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(b.x, b.y, BALL_R, 0, TAU);
      ctx.fill();

      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      ctx.arc(b.x - 2.2, b.y - 2.6, 1.5, 0, TAU);
      ctx.fill();

      glow.globalAlpha = 0.55;
      glow.fillStyle = '#cfe9ff';
      glow.beginPath();
      glow.arc(b.x, b.y, BALL_R * 0.9, 0, TAU);
      glow.fill();
      glow.globalAlpha = 1;
    }
  }

  _drawBanner(ctx) {
    const b = this.banner;
    if (!b) return;
    const k = b.life / b.max;
    const inK = clamp((1 - k) * 6, 0, 1);
    const scale = b.big ? lerp(0.4, 1, easeOutBack(inK)) : lerp(0.7, 1, easeOutBack(inK));
    const alpha = k < 0.2 ? k / 0.2 : 1;
    const y = b.big ? 430 : 400;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(W / 2, y);
    ctx.scale(scale, scale);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const size = b.big ? 44 : 28;
    ctx.font = `900 ${size}px system-ui, -apple-system, sans-serif`;

    if (b.big) {
      const wob = Math.sin(this.frame * 0.2) * 0.03;
      ctx.rotate(wob);
      // 色収差風の三重描き
      ctx.globalAlpha = alpha * 0.75;
      ctx.fillStyle = '#00e5ff';
      ctx.fillText(b.text, -3, 0);
      ctx.fillStyle = '#ff2d7a';
      ctx.fillText(b.text, 3, 0);
      ctx.globalAlpha = alpha;
    }
    ctx.lineWidth = 8;
    ctx.strokeStyle = 'rgba(0,0,0,0.8)';
    ctx.strokeText(b.text, 0, 0);
    const grd = ctx.createLinearGradient(0, -size / 2, 0, size / 2);
    grd.addColorStop(0, '#ffffff');
    grd.addColorStop(0.5, b.color);
    grd.addColorStop(1, '#ffffff');
    ctx.fillStyle = grd;
    ctx.fillText(b.text, 0, 0);
    ctx.restore();
  }
}

// ---------------------------------------------------------------- 補助

function easeOutBack(x) {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
